import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchTaskThread, openTaskFile, postTaskReply, uploadTaskFile } from '../../services/assignments';
import PrivacyNoticeLink from '../PrivacyNoticeLink';
import './LearningTasks.css';

const ROLE_LABELS = { teacher: 'Teacher', student: 'Student', family: 'Family' };

export function AttachmentChip({ attachment, taskId, onRemove }: any) {
  const [error, setError] = useState<any>('');
  const open = async () => {
    setError('');
    try {
      await openTaskFile(attachment, taskId);
    } catch (e: any) {
      setError(e.message);
    }
  };
  let content;
  if (attachment.type === 'link') {
    content = <a href={attachment.url} target="_blank" rel="noopener noreferrer">🔗 {attachment.name}</a>;
  } else if (attachment.key) {
    content = <button type="button" className="lt-chip-open" onClick={open}>📄 {attachment.name}</button>;
  } else {
    content = <span title="This file was added before uploads were saved. Remove it and attach it again.">📄 {attachment.name} (not uploaded)</span>;
  }
  return (
    <span className="lt-chip">
      {content}
      {onRemove && (
        <button type="button" className="lt-chip-remove" aria-label={`Remove ${attachment.name}`} onClick={onRemove}>✕</button>
      )}
      {error && <span role="alert" className="lt-error"> {error}</span>}
    </span>
  );
}

export function AttachmentList({ attachments, taskId, onRemove }: any) {
  if (!attachments?.length) return null;
  return (
    <div className="lt-attachments">
      {attachments.map((att, idx) => (
        <AttachmentChip
          key={att.key || att.url || `${att.name}-${idx}`}
          attachment={att}
          taskId={taskId}
          onRemove={onRemove ? () => onRemove(idx) : undefined}
        />
      ))}
    </div>
  );
}

export function formatWhen(value) {
  if (!value) return '';
  // Server timestamps are UTC; older rows may lack a timezone suffix.
  const text = String(value);
  const date = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(text) ? text : `${text}Z`);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Private conversation for one student on a learning task.
 * channel "student": the student's submission and conversation with their teachers (families can read it once graded).
 * channel "family": the family's comments to the teachers after grading (not shown to the student).
 */
export function TaskReplies({
  taskId, studentId, channel = 'student', canReply = true, title = 'Replies', emptyText = 'No replies yet.', readOnlyText = '', onPosted,
}: any) {
  const [replies, setReplies] = useState<any>([]);
  const [loading, setLoading] = useState<any>(true);
  const [body, setBody] = useState<any>('');
  const [files, setFiles] = useState<any>([]);
  const [busy, setBusy] = useState<any>(false);
  const [error, setError] = useState<any>('');
  const fileInput = useRef<any>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReplies(await fetchTaskThread(taskId, studentId, channel));
      setError('');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [taskId, studentId, channel]);

  useEffect(() => { load(); }, [load]);

  const attach = async (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';
    if (!picked.length) return;
    setBusy(true);
    setError('');
    try {
      const uploaded = [];
      for (const file of picked) uploaded.push(await uploadTaskFile(file));
      setFiles((prev) => [...prev, ...uploaded]);
    } catch (e: any) {
      setError(`Could not upload file: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const send = async (event) => {
    event.preventDefault();
    if (!body.trim() && !files.length) return;
    setBusy(true);
    setError('');
    try {
      const reply = await postTaskReply(taskId, studentId, { body: body.trim(), attachments: files }, channel);
      setReplies((prev) => [...prev, reply]);
      setBody('');
      setFiles([]);
      onPosted?.(reply);
    } catch (e: any) {
      setError(`Could not send reply: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="lt-thread" aria-label={title}>
      <h4 className="lt-thread-title">{title}</h4>
      {loading && <p className="lt-muted" role="status">Loading replies...</p>}
      {!loading && replies.length === 0 && <p className="lt-muted">{emptyText}</p>}
      {!loading && replies.length > 0 && (
        <ul className="lt-replies">
          {replies.map((reply) => (
            <li key={reply.id} className={`lt-reply lt-reply-${reply.authorRole}`}>
              <div className="lt-reply-meta">
                <strong>{reply.authorName}</strong>
                <span className="lt-role">{ROLE_LABELS[reply.authorRole] || reply.authorRole}</span>
                <span className="lt-muted">{formatWhen(reply.createdAt)}</span>
              </div>
              {reply.body && <p className="lt-reply-body">{reply.body}</p>}
              <AttachmentList attachments={reply.attachments} taskId={taskId} />
            </li>
          ))}
        </ul>
      )}
      {!canReply && readOnlyText && <p className="lt-muted lt-readonly-note">{readOnlyText}</p>}
      {canReply && (
        <form className="lt-reply-form" onSubmit={send}>
          <textarea
            aria-label="Write a reply"
            placeholder="Write a reply..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={2}
          />
          <AttachmentList attachments={files} onRemove={(idx) => setFiles((prev) => prev.filter((_, i) => i !== idx))} />
          <div className="lt-reply-actions">
            <input ref={fileInput} type="file" multiple hidden onChange={attach} aria-label="Attach files to reply" />
            <button type="button" className="lt-btn" onClick={() => fileInput.current?.click()} disabled={busy}>
              📎 Attach file
            </button>
            <button type="submit" className="lt-btn lt-btn-primary" disabled={busy || (!body.trim() && !files.length)}>
              {busy ? 'Sending...' : 'Send reply'}
            </button>
          </div>
          <div style={{ marginTop: 8 }}><PrivacyNoticeLink /></div>
        </form>
      )}
      {error && <p role="alert" className="lt-error">{error}</p>}
    </section>
  );
}
